/**
 * Feature flags de Remote Config que gobiernan el backend dual (callable vs REST).
 *
 * Port de `remote_config_service.dart`. Todos OFF por defecto (rollout gradual
 * 1%→5%→25%→100%). Las LECTURAS de streams en tiempo real SIEMPRE van por RTDB,
 * pase lo que pase con estos flags (ver rtdb-contracts / "regla de oro").
 */

export const FLAG_DEFAULTS = {
  /** Habilita el cliente REST NestJS en general (gate del ApiClient). */
  useNestApi: false,
  /** Auth contra NestJS (canje idToken→JWT) en vez de solo Firebase Auth. */
  useNestAuth: false,
  /** Maps (geocode/autocomplete/details/routes) por REST en vez de callable. */
  useRestMaps: false,
  /** Comandos de viaje (crear/fase/cancelar) por REST. */
  useRestRides: false,
  /** Comandos de reserva por REST. */
  useRestReserves: false,
  /** Escrituras de usuario (perfil, push tokens) por REST. */
  useRestUsers: false,
  /** Flujo guest (quote/otp/createReserve) por REST. */
  useRestGuest: false,
  /**
   * Permite usar las representaciones fiscales canónicas VERI*FACTU.
   * OFF localmente y en Remote Config por defecto: no se genera PDF local como
   * alternativa si el backend fiscal no está disponible.
   */
  useVerifactuInvoices: false,
  /**
   * Habilita el recovery OTP/token opaco de facturas guest. Es un gate
   * independiente y también queda OFF por defecto.
   */
  useGuestInvoiceAccess: false,
} as const;

export type FlagName = keyof typeof FLAG_DEFAULTS;

export const FLAG_NAMES = Object.keys(FLAG_DEFAULTS) as FlagName[];

/** Intervalo mínimo de fetch de Remote Config: 12h en prod, 0 en dev/staging. */
export const REMOTE_CONFIG_MIN_FETCH_MS = { prod: 12 * 60 * 60 * 1000, devOrStaging: 0 } as const;
export const REMOTE_CONFIG_FETCH_TIMEOUT_MS = 10_000;

/** Fuente de flags resuelta (Remote Config en prod; un objeto fijo en tests). */
export interface RemoteFlags {
  getBool(name: FlagName): boolean;
}

/** Implementación de {@link RemoteFlags} sobre un mapa plano (tests / overrides). */
export class StaticFlags implements RemoteFlags {
  private readonly values: Record<FlagName, boolean>;
  constructor(overrides: Partial<Record<FlagName, boolean>> = {}) {
    this.values = { ...FLAG_DEFAULTS, ...overrides };
  }
  getBool(name: FlagName): boolean {
    return this.values[name];
  }
}
